/**
 * Outgoing webhooks of one owner (`platform` or a customer id): endpoints with their state,
 * the create / edit dialog (secret write-only), "send test event", and the delivery log with
 * the exact body sent and resend.
 */

import { Pencil, Plus, RefreshCw, RotateCw, Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import type { WebhookDelivery, WebhookEndpoint, WebhookEndpointInput, WebhookEventSpec } from '@/api/areas/webhooks';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EmptyState, Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useApi } from '@/lib/useApi';
import { ActionDialog, FormActions, When } from '../compliance/ui';
import { SwitchRow } from '../integrations/shared';

type SecretAction = 'KEEP' | 'SET' | 'CLEAR';

/** The catalog grouped by the event's object (`domain`, `transfer_out`, `order`, …), in catalog order. */
function groupCatalog(catalog: WebhookEventSpec[]): [string, WebhookEventSpec[]][] {
  const groups = new Map<string, WebhookEventSpec[]>();
  for (const c of catalog) {
    const g = c.name.split('.')[0] ?? c.name;
    groups.set(g, [...(groups.get(g) ?? []), c]);
  }
  return [...groups.entries()];
}

function EndpointForm({
  owner,
  endpoint,
  catalog,
  onDone,
  onCancel,
}: {
  owner: string;
  endpoint: WebhookEndpoint | null;
  catalog: WebhookEventSpec[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation('work');
  const { t: tc } = useTranslation();
  const [description, setDescription] = useState(endpoint?.description ?? '');
  const [url, setUrl] = useState(endpoint?.url ?? 'https://');
  const [enabled, setEnabled] = useState(endpoint?.enabled ?? true);
  const [events, setEvents] = useState<string[]>(endpoint?.events ?? ['*']);
  const [secretAction, setSecretAction] = useState<SecretAction>(endpoint?.has_secret ? 'KEEP' : 'SET');
  const [secret, setSecret] = useState('');
  const write = useWrite((b: WebhookEndpointInput, opts) =>
    endpoint ? api.updateWebhookEndpoint(owner, endpoint.id, b, opts) : api.createWebhookEndpoint(owner, b, opts),
  );
  const all = events.includes('*');
  const toggle = (name: string, on: boolean) => setEvents((e) => (on ? [...e.filter((x) => x !== '*'), name] : e.filter((x) => x !== name)));
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        const body: WebhookEndpointInput = {
          description,
          url,
          enabled,
          events,
          secret: secretAction === 'SET' ? { action: 'SET', value: secret } : { action: secretAction },
          expected_version: endpoint?.version,
        };
        if (await write.run(body)) onDone();
      }}
    >
      <Field label={t('webhooks.form.description')}>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} disabled={write.busy} />
      </Field>
      <Field label={t('webhooks.form.url')} hint={t('webhooks.form.urlHint')}>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} disabled={write.busy} inputMode="url" />
      </Field>
      <Field label={t('webhooks.form.events')}>
        <div className="grid gap-2 text-sm">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child */}
          <label className="flex items-center gap-2">
            <Checkbox checked={all} onCheckedChange={(v) => setEvents(v === true ? ['*'] : [])} disabled={write.busy} />
            {t('webhooks.form.all')}
          </label>
          {groupCatalog(catalog).map(([group, specs]) => (
            <fieldset key={group} className="grid gap-2 rounded-md border p-2">
              <legend className="px-1 text-xs font-medium text-muted-foreground">
                {t(`webhooks.groups.${group}`, { defaultValue: group })}
              </legend>
              {specs.map((c) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child
                <label key={c.name} className="flex items-start gap-2">
                  <Checkbox checked={all || events.includes(c.name)} disabled={all || write.busy} onCheckedChange={(v) => toggle(c.name, v === true)} className="mt-0.5" />
                  <span className="min-w-0">
                    <code className="text-xs break-all">{c.name}</code>
                    {c.audience === 'PLATFORM' ? <span className="ml-1 text-[10px] uppercase text-muted-foreground">{t('webhooks.platformOnly')}</span> : null}
                    <span className="block text-xs text-muted-foreground">{i18n.language === 'en' ? c.description_en : c.description_vi}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          ))}
        </div>
      </Field>
      <SwitchRow label={t('webhooks.form.enabled')} checked={enabled} onChange={setEnabled} disabled={write.busy} />
      <Field label={t('webhooks.form.secret')} hint={t('webhooks.form.secretHint')}>
        <div className="grid gap-2">
          <select
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            value={secretAction}
            onChange={(e) => setSecretAction(e.target.value as SecretAction)}
            disabled={write.busy}
            aria-label={t('webhooks.form.secret')}
          >
            {endpoint?.has_secret ? <option value="KEEP">{t('webhooks.form.secretKeep')}</option> : null}
            <option value="SET">{t('webhooks.form.secretSet')}</option>
            <option value="CLEAR">{t('webhooks.form.secretClear')}</option>
          </select>
          {secretAction === 'SET' ? <Input type="password" autoComplete="new-password" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={write.busy} /> : null}
        </div>
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !description.trim() || events.length === 0 || (secretAction === 'SET' && secret.length < 16)}>
          {t('webhooks.form.save')}
        </Button>
      </FormActions>
    </form>
  );
}

function DeliveryLog({ owner, reloadKey }: { owner: string; reloadKey: number }) {
  const { t } = useTranslation('work');
  const { t: tc } = useTranslation();
  const result = useApi((signal) => api.listWebhookDeliveries(owner, { limit: 100 }, { signal }), [owner, reloadKey]);
  const [open, setOpen] = useState<WebhookDelivery | null>(null);
  const resend = useWrite((id: string, opts) => api.resendWebhookDelivery(owner, id, opts));
  const tone = (s: WebhookDelivery['status']) => (s === 'DELIVERED' ? 'ok' : s === 'FAILED' ? 'crit' : 'pending');
  const columns: Column<WebhookDelivery>[] = [
    {
      key: 'event',
      header: t('webhooks.log.col.event'),
      cell: (d) => (
        <Button variant="link" size="sm" className="h-auto p-0 font-mono text-xs" onClick={() => setOpen(d)}>
          {d.event_type}
        </Button>
      ),
    },
    { key: 'endpoint', header: t('webhooks.log.col.endpoint'), cell: (d) => <span className="text-sm">{d.endpoint_description}</span> },
    { key: 'status', header: t('webhooks.log.col.status'), cell: (d) => <Badge tone={tone(d.status)}>{t(`webhooks.log.status.${d.status}`)}</Badge> },
    { key: 'attempts', header: t('webhooks.log.col.attempts'), numeric: true, cell: (d) => `${d.attempts}/${d.max_attempts}` },
    { key: 'last', header: t('webhooks.log.col.last'), cell: (d) => <span className="text-xs">{d.last_error ?? (d.last_status_code ? `HTTP ${d.last_status_code}` : '—')}</span> },
    { key: 'created', header: t('webhooks.log.col.created'), cell: (d) => <When at={d.created_at} /> },
    {
      key: 'resend',
      header: '',
      cell: (d) =>
        d.status === 'FAILED' || d.status === 'DELIVERED' ? (
          <Button size="sm" variant="ghost" disabled={resend.busy} onClick={async () => (await resend.run(d.id)) && result.reload()}>
            <RotateCw /> {t('webhooks.log.resend')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Section
      flush
      title={t('webhooks.log.title')}
      actions={
        <Button size="sm" variant="outline" onClick={result.reload}>
          <RefreshCw /> {tc('action.refresh')}
        </Button>
      }
    >
      {resend.state.phase === 'error' ? (
        <div className="px-5">
          <WriteError error={resend.state.error} />
        </div>
      ) : null}
      {result.status === 'loading' ? (
        <Loading rows={3} />
      ) : result.status === 'error' ? (
        <div className="px-5 pb-5">
          <DataError error={result.error} onRetry={result.reload} />
        </div>
      ) : (
        <DataTable columns={columns} rows={result.data.items} rowKey={(d) => d.id} empty={<EmptyState title={t('webhooks.log.empty')} />} />
      )}
      <ActionDialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)} title={t('webhooks.log.body')} wide>
        <pre className="max-h-[60vh] overflow-auto rounded-md bg-muted p-3 text-xs">{open ? JSON.stringify(open.body, null, 2) : ''}</pre>
      </ActionDialog>
    </Section>
  );
}

export function WebhooksPanel({ owner }: { owner: string }) {
  const { t } = useTranslation('work');
  const result = useApi((signal) => api.listWebhookEndpoints(owner, { signal }), [owner]);
  const [editing, setEditing] = useState<WebhookEndpoint | 'new' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const test = useWrite((id: string, opts) => api.testWebhookEndpoint(owner, id, opts));
  if (result.status === 'loading') return <Loading rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  const { items, catalog, disable_after } = result.data;
  const columns: Column<WebhookEndpoint>[] = [
    {
      key: 'endpoint',
      header: t('webhooks.col.endpoint'),
      cell: (e) => (
        <div className="min-w-0 text-sm">
          <span className="font-medium">{e.description}</span>
          <div className="truncate font-mono text-xs text-muted-foreground" title={e.url}>
            {e.url}
          </div>
          <div className="text-xs text-muted-foreground">{e.has_secret ? t('webhooks.secretSet') : t('webhooks.secretNone')}</div>
        </div>
      ),
    },
    { key: 'events', header: t('webhooks.col.events'), cell: (e) => <span className="text-xs">{e.events.includes('*') ? t('webhooks.allEvents') : e.events.join(', ')}</span> },
    {
      key: 'state',
      header: t('webhooks.col.state'),
      cell: (e) => (
        <div className="space-y-1">
          <Badge tone={e.enabled ? 'ok' : e.disabled_at ? 'crit' : 'neutral'}>{e.enabled ? t('webhooks.enabled') : e.disabled_at ? t('webhooks.autoDisabled') : t('webhooks.disabled')}</Badge>
          {e.consecutive_failures > 0 ? <div className="text-xs text-muted-foreground">{t('webhooks.failing', { count: e.consecutive_failures })}</div> : null}
        </div>
      ),
    },
    { key: 'last', header: t('webhooks.col.lastSuccess'), cell: (e) => <When at={e.last_success_at} /> },
    {
      key: 'actions',
      header: '',
      cell: (e) => (
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" onClick={() => setEditing(e)}>
            <Pencil /> {t('webhooks.edit')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={test.busy}
            onClick={async () => {
              if (await test.run(e.id)) {
                setNotice(t('webhooks.testQueued'));
                setReloadKey((k) => k + 1);
              }
            }}
          >
            <Send /> {t('webhooks.test')}
          </Button>
        </div>
      ),
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-6">
      <Section
        flush
        title={owner === 'platform' ? t('webhooks.title') : t('webhooks.customerTitle')}
        description={owner === 'platform' ? t('webhooks.subtitle') : t('webhooks.customerSubtitle')}
        actions={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus /> {t('webhooks.add')}
          </Button>
        }
      >
        <div className="grid gap-3 px-5 pb-3">
          <Callout tone="info">{t('webhooks.disableNote', { failures: disable_after.failures, hours: disable_after.hours })}</Callout>
          {notice ? <Callout tone="ok">{notice}</Callout> : null}
          {test.state.phase === 'error' ? <WriteError error={test.state.error} /> : null}
        </div>
        <DataTable columns={columns} rows={items} rowKey={(e) => e.id} empty={<EmptyState title={t('webhooks.empty')} />} />
      </Section>
      <DeliveryLog owner={owner} reloadKey={reloadKey} />
      <ActionDialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} title={editing === 'new' ? t('webhooks.add') : t('webhooks.edit')} wide>
        {editing !== null ? (
          <EndpointForm
            owner={owner}
            endpoint={editing === 'new' ? null : editing}
            catalog={catalog}
            onCancel={() => setEditing(null)}
            onDone={() => {
              setEditing(null);
              result.reload();
            }}
          />
        ) : null}
      </ActionDialog>
    </div>
  );
}
